import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { Framework, GRCControl } from '../../types/grc';
import {
  X, ChevronRight, ChevronLeft, Check, AlertCircle, 
  Building2, Users, Shield, Database, Cloud, CreditCard,
  Globe, Server, Lock, FileText, HelpCircle
} from 'lucide-react';

// Framework-specific question sets
interface QuestionOption {
  id: string;
  label: string;
  labelSr?: string;
  description?: string;
  descriptionSr?: string;
  impacts: string[]; // Control codes that become applicable
  excludes?: string[]; // Control codes that become not applicable
}

interface Question {
  id: string;
  category: 'organization' | 'data' | 'systems' | 'compliance' | 'operations';
  categoryIcon: React.ReactNode;
  question: string;
  questionSr: string;
  helpText?: string;
  helpTextSr?: string;
  type: 'single' | 'multiple' | 'yes_no' | 'scale';
  options?: QuestionOption[];
  required?: boolean;
}

// Question sets per framework type
const ISO_27001_QUESTIONS: Question[] = [
  {
    id: 'org_size',
    category: 'organization',
    categoryIcon: <Building2 className="w-5 h-5" />,
    question: 'What is the size of your organization?',
    questionSr: 'Koja je veličina vaše organizacije?',
    type: 'single',
    required: true,
    options: [
      { id: 'small', label: 'Small (1-50 employees)', labelSr: 'Mala (1-50 zaposlenih)', impacts: [] },
      { id: 'medium', label: 'Medium (51-250 employees)', labelSr: 'Srednja (51-250 zaposlenih)', impacts: ['A.7.2', 'A.7.3'] },
      { id: 'large', label: 'Large (250+ employees)', labelSr: 'Velika (250+ zaposlenih)', impacts: ['A.7.2', 'A.7.3', 'A.8.1', 'A.8.2'] },
    ]
  },
  {
    id: 'data_processing',
    category: 'data',
    categoryIcon: <Database className="w-5 h-5" />,
    question: 'What type of data does your organization process?',
    questionSr: 'Koje tipove podataka vaša organizacija obrađuje?',
    type: 'multiple',
    required: true,
    options: [
      { id: 'personal', label: 'Personal Data (PII)', labelSr: 'Lični podaci (PII)', impacts: ['A.5.33', 'A.5.34', 'A.7.4'] },
      { id: 'financial', label: 'Financial Data', labelSr: 'Finansijski podaci', impacts: ['A.5.33', 'A.8.11', 'A.8.12'] },
      { id: 'health', label: 'Health Data', labelSr: 'Zdravstveni podaci', impacts: ['A.5.33', 'A.5.34', 'A.8.11'] },
      { id: 'payment', label: 'Payment Card Data', labelSr: 'Podaci platnih kartica', impacts: ['A.8.11', 'A.8.12', 'A.8.24'] },
      { id: 'classified', label: 'Classified/Confidential', labelSr: 'Klasifikovani/Poverljivi', impacts: ['A.5.12', 'A.5.13', 'A.5.14'] },
    ]
  },
  {
    id: 'cloud_usage',
    category: 'systems',
    categoryIcon: <Cloud className="w-5 h-5" />,
    question: 'Do you use cloud services?',
    questionSr: 'Da li koristite cloud servise?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['A.5.21', 'A.5.22', 'A.5.23', 'A.8.26'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [], excludes: ['A.5.21', 'A.5.22', 'A.5.23'] },
    ]
  },
  {
    id: 'remote_work',
    category: 'operations',
    categoryIcon: <Users className="w-5 h-5" />,
    question: 'Does your organization support remote work?',
    questionSr: 'Da li vaša organizacija podržava rad na daljinu?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['A.6.7', 'A.8.1', 'A.8.20'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [], excludes: ['A.6.7'] },
    ]
  },
  {
    id: 'physical_locations',
    category: 'organization',
    categoryIcon: <Building2 className="w-5 h-5" />,
    question: 'How many physical locations does your organization have?',
    questionSr: 'Koliko fizičkih lokacija ima vaša organizacija?',
    type: 'single',
    options: [
      { id: 'single', label: 'Single location', labelSr: 'Jedna lokacija', impacts: ['A.7.1', 'A.7.2'] },
      { id: 'multiple', label: 'Multiple locations', labelSr: 'Više lokacija', impacts: ['A.7.1', 'A.7.2', 'A.7.3', 'A.7.4'] },
      { id: 'datacenter', label: 'Including data centers', labelSr: 'Uključujući data centre', impacts: ['A.7.1', 'A.7.2', 'A.7.3', 'A.7.4', 'A.7.5', 'A.7.6'] },
    ]
  },
  {
    id: 'supplier_access',
    category: 'compliance',
    categoryIcon: <Shield className="w-5 h-5" />,
    question: 'Do external suppliers have access to your systems?',
    questionSr: 'Da li eksterni dobavljači imaju pristup vašim sistemima?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['A.5.19', 'A.5.20', 'A.5.21', 'A.5.22'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [], excludes: ['A.5.19', 'A.5.20'] },
    ]
  },
  {
    id: 'mobile_devices',
    category: 'systems',
    categoryIcon: <Server className="w-5 h-5" />,
    question: 'Are mobile devices used for business purposes?',
    questionSr: 'Da li se mobilni uređaji koriste u poslovne svrhe?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['A.8.1', 'A.6.7', 'A.8.20'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [], excludes: ['A.8.1'] },
    ]
  },
  {
    id: 'development',
    category: 'operations',
    categoryIcon: <Lock className="w-5 h-5" />,
    question: 'Does your organization develop software?',
    questionSr: 'Da li vaša organizacija razvija softver?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['A.8.25', 'A.8.26', 'A.8.27', 'A.8.28', 'A.8.29', 'A.8.30', 'A.8.31', 'A.8.32', 'A.8.33'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [], excludes: ['A.8.25', 'A.8.26', 'A.8.27', 'A.8.28', 'A.8.29', 'A.8.30', 'A.8.31', 'A.8.32', 'A.8.33'] },
    ]
  },
];

const ZIB_QUESTIONS: Question[] = [
  {
    id: 'ikt_category',
    category: 'organization',
    categoryIcon: <Building2 className="w-5 h-5" />,
    question: 'What is your organization\'s IKT system category?',
    questionSr: 'Koja je kategorija vašeg IKT sistema?',
    helpText: 'Based on Zakon o informacionoj bezbednosti, Articles 5-10',
    helpTextSr: 'Na osnovu Zakona o informacionoj bezbednosti, Članovi 5-10',
    type: 'single',
    required: true,
    options: [
      { id: 'cat1', label: 'Category I - Critical Infrastructure', labelSr: 'Kategorija I - Kritična infrastruktura', impacts: ['ZIB-5.1', 'ZIB-6.1', 'ZIB-14.1', 'ZIB-34.1'] },
      { id: 'cat2', label: 'Category II - Important Systems', labelSr: 'Kategorija II - Važni sistemi', impacts: ['ZIB-5.1', 'ZIB-6.1', 'ZIB-14.1'] },
      { id: 'cat3', label: 'Category III - Standard Systems', labelSr: 'Kategorija III - Standardni sistemi', impacts: ['ZIB-5.1'] },
    ]
  },
  {
    id: 'operator_type',
    category: 'organization',
    categoryIcon: <Shield className="w-5 h-5" />,
    question: 'Are you an operator of essential services?',
    questionSr: 'Da li ste operator suštinskih usluga?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['ZIB-8.1', 'ZIB-9.1', 'ZIB-10.1', 'ZIB-17.1', 'ZIB-18.1'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'zzpl_applies',
    category: 'data',
    categoryIcon: <FileText className="w-5 h-5" />,
    question: 'Does ZZPL (Data Protection Law) apply to your organization?',
    questionSr: 'Da li se ZZPL (Zakon o zaštiti podataka) primenjuje na vašu organizaciju?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['ZIB-4.1', 'ZIB-4.2', 'ZIB-4.3'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'incident_response',
    category: 'operations',
    categoryIcon: <AlertCircle className="w-5 h-5" />,
    question: 'Do you have an incident response team?',
    questionSr: 'Da li imate tim za odgovor na incidente?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['ZIB-20.1', 'ZIB-21.1', 'ZIB-22.1'] },
      { id: 'no', label: 'No - Need to establish', labelSr: 'Ne - potrebno je uspostaviti', impacts: ['ZIB-19.1', 'ZIB-20.1'] },
    ]
  },
];

const GDPR_QUESTIONS: Question[] = [
  {
    id: 'data_controller',
    category: 'data',
    categoryIcon: <Database className="w-5 h-5" />,
    question: 'Is your organization a Data Controller or Processor?',
    questionSr: 'Da li je vaša organizacija Rukovalac ili Obrađivač podataka?',
    type: 'single',
    required: true,
    options: [
      { id: 'controller', label: 'Data Controller', labelSr: 'Rukovalac podataka', impacts: ['GDPR-5', 'GDPR-6', 'GDPR-7', 'GDPR-12', 'GDPR-13', 'GDPR-14'] },
      { id: 'processor', label: 'Data Processor', labelSr: 'Obrađivač podataka', impacts: ['GDPR-28', 'GDPR-29', 'GDPR-30'] },
      { id: 'both', label: 'Both', labelSr: 'Obe uloge', impacts: ['GDPR-5', 'GDPR-6', 'GDPR-7', 'GDPR-12', 'GDPR-13', 'GDPR-14', 'GDPR-28', 'GDPR-29', 'GDPR-30'] },
    ]
  },
  {
    id: 'special_categories',
    category: 'data',
    categoryIcon: <Lock className="w-5 h-5" />,
    question: 'Do you process special categories of personal data?',
    questionSr: 'Da li obrađujete posebne kategorije ličnih podataka?',
    helpText: 'Health, biometric, genetic, racial/ethnic origin, political opinions, etc.',
    helpTextSr: 'Zdravlje, biometrija, genetski podaci, rasno/etničko poreklo, politička mišljenja, itd.',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['GDPR-9', 'GDPR-35', 'GDPR-37'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'large_scale',
    category: 'organization',
    categoryIcon: <Users className="w-5 h-5" />,
    question: 'Do you process personal data on a large scale?',
    questionSr: 'Da li obrađujete lične podatke u velikom obimu?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['GDPR-35', 'GDPR-37', 'GDPR-38', 'GDPR-39'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'cross_border',
    category: 'compliance',
    categoryIcon: <Globe className="w-5 h-5" />,
    question: 'Do you transfer data outside the EU/EEA?',
    questionSr: 'Da li prenosite podatke van EU/EEA?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['GDPR-44', 'GDPR-45', 'GDPR-46', 'GDPR-47', 'GDPR-49'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'automated_decisions',
    category: 'operations',
    categoryIcon: <Server className="w-5 h-5" />,
    question: 'Do you use automated decision-making including profiling?',
    questionSr: 'Da li koristite automatizovano odlučivanje uključujući profilisanje?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['GDPR-22', 'GDPR-35'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
];

const NIS2_QUESTIONS: Question[] = [
  {
    id: 'entity_type',
    category: 'organization',
    categoryIcon: <Building2 className="w-5 h-5" />,
    question: 'What type of entity is your organization?',
    questionSr: 'Koja je vrsta vašeg entiteta?',
    type: 'single',
    required: true,
    options: [
      { id: 'essential', label: 'Essential Entity', labelSr: 'Suštinski entitet', impacts: ['NIS2-21', 'NIS2-23', 'NIS2-29', 'NIS2-32', 'NIS2-34'] },
      { id: 'important', label: 'Important Entity', labelSr: 'Važan entitet', impacts: ['NIS2-21', 'NIS2-23', 'NIS2-33'] },
      { id: 'supplier', label: 'Critical Supplier', labelSr: 'Kritični dobavljač', impacts: ['NIS2-21', 'NIS2-22'] },
    ]
  },
  {
    id: 'sector',
    category: 'organization',
    categoryIcon: <Shield className="w-5 h-5" />,
    question: 'Which sector does your organization belong to?',
    questionSr: 'Kom sektoru pripada vaša organizacija?',
    type: 'single',
    required: true,
    options: [
      { id: 'energy', label: 'Energy', labelSr: 'Energetika', impacts: ['NIS2-A1'] },
      { id: 'transport', label: 'Transport', labelSr: 'Transport', impacts: ['NIS2-A1'] },
      { id: 'banking', label: 'Banking/Finance', labelSr: 'Bankarstvo/Finansije', impacts: ['NIS2-A1'] },
      { id: 'health', label: 'Healthcare', labelSr: 'Zdravstvo', impacts: ['NIS2-A1'] },
      { id: 'digital', label: 'Digital Infrastructure', labelSr: 'Digitalna infrastruktura', impacts: ['NIS2-A1'] },
      { id: 'other', label: 'Other', labelSr: 'Ostalo', impacts: [] },
    ]
  },
  {
    id: 'supply_chain',
    category: 'operations',
    categoryIcon: <Users className="w-5 h-5" />,
    question: 'Do you manage critical supply chain dependencies?',
    questionSr: 'Da li upravljate kritičnim zavisnostima lanca snabdevanja?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['NIS2-21.2', 'NIS2-22'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
];

const PCI_DSS_QUESTIONS: Question[] = [
  {
    id: 'merchant_level',
    category: 'organization',
    categoryIcon: <CreditCard className="w-5 h-5" />,
    question: 'What is your PCI DSS merchant level?',
    questionSr: 'Koji je vaš PCI DSS nivo trgovca?',
    type: 'single',
    required: true,
    options: [
      { id: 'level1', label: 'Level 1 (6M+ transactions)', labelSr: 'Nivo 1 (6M+ transakcija)', impacts: ['PCI-1', 'PCI-2', 'PCI-3', 'PCI-11.3', 'PCI-12.2'] },
      { id: 'level2', label: 'Level 2 (1-6M transactions)', labelSr: 'Nivo 2 (1-6M transakcija)', impacts: ['PCI-1', 'PCI-2', 'PCI-3', 'PCI-11.3'] },
      { id: 'level3', label: 'Level 3 (20K-1M transactions)', labelSr: 'Nivo 3 (20K-1M transakcija)', impacts: ['PCI-1', 'PCI-2', 'PCI-3'] },
      { id: 'level4', label: 'Level 4 (<20K transactions)', labelSr: 'Nivo 4 (<20K transakcija)', impacts: ['PCI-1', 'PCI-2'] },
    ]
  },
  {
    id: 'card_storage',
    category: 'data',
    categoryIcon: <Database className="w-5 h-5" />,
    question: 'Do you store cardholder data?',
    questionSr: 'Da li skladištite podatke kartica?',
    type: 'yes_no',
    required: true,
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['PCI-3', 'PCI-3.1', 'PCI-3.2', 'PCI-3.3', 'PCI-3.4', 'PCI-3.5', 'PCI-3.6', 'PCI-3.7'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'wireless',
    category: 'systems',
    categoryIcon: <Server className="w-5 h-5" />,
    question: 'Do you use wireless networks in the cardholder data environment?',
    questionSr: 'Da li koristite bežične mreže u okruženju sa podacima kartica?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['PCI-1.2.3', 'PCI-2.1.1', 'PCI-4.1.1', 'PCI-9.1.3', 'PCI-11.1'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
  {
    id: 'ecommerce',
    category: 'operations',
    categoryIcon: <Globe className="w-5 h-5" />,
    question: 'Do you process e-commerce transactions?',
    questionSr: 'Da li obrađujete e-commerce transakcije?',
    type: 'yes_no',
    options: [
      { id: 'yes', label: 'Yes', labelSr: 'Da', impacts: ['PCI-6.4', 'PCI-6.5', 'PCI-11.5'] },
      { id: 'no', label: 'No', labelSr: 'Ne', impacts: [] },
    ]
  },
];

// Map framework codes to question sets
const FRAMEWORK_QUESTIONS: Record<string, Question[]> = {
  'ISO 27001': ISO_27001_QUESTIONS,
  'ISO27001': ISO_27001_QUESTIONS,
  'ZIB': ZIB_QUESTIONS,
  'GDPR': GDPR_QUESTIONS,
  'NIS2': NIS2_QUESTIONS,
  'PCI-DSS': PCI_DSS_QUESTIONS,
  'PCI DSS': PCI_DSS_QUESTIONS,
};

interface FrameworkQuestionnaireProps {
  framework: Framework;
  controls: GRCControl[];
  isOpen: boolean;
  onClose: () => void;
  onComplete: (answers: Record<string, string | string[]>, applicableControls: string[]) => void;
}

export const FrameworkQuestionnaire: React.FC<FrameworkQuestionnaireProps> = ({
  framework,
  controls,
  isOpen,
  onClose,
  onComplete
}) => {
  const { t, language } = useI18n();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [showResults, setShowResults] = useState(false);

  // Get questions for this framework
  const questions = FRAMEWORK_QUESTIONS[framework.code] || ISO_27001_QUESTIONS;

  // Calculate applicable controls based on answers
  const calculateApplicableControls = (): string[] => {
    const applicable = new Set<string>();
    const excluded = new Set<string>();

    Object.entries(answers).forEach(([questionId, answer]) => {
      const question = questions.find(q => q.id === questionId);
      if (!question?.options) return;

      const selectedOptions = Array.isArray(answer) ? answer : [answer];
      selectedOptions.forEach(optId => {
        const option = question.options?.find(o => o.id === optId);
        if (option) {
          option.impacts.forEach(code => applicable.add(code));
          option.excludes?.forEach(code => excluded.add(code));
        }
      });
    });

    // Remove excluded from applicable
    excluded.forEach(code => applicable.delete(code));

    return Array.from(applicable);
  };

  const handleAnswer = (questionId: string, value: string | string[]) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (showResults) {
      setShowResults(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    const applicableControls = calculateApplicableControls();
    onComplete(answers, applicableControls);
    onClose();
  };

  const currentQuestion = questions[currentStep];
  const canProceed = !currentQuestion?.required || answers[currentQuestion?.id];
  const applicableControls = calculateApplicableControls();
  const progress = ((currentStep + 1) / questions.length) * 100;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t.questionnaire.title} - {framework.code}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {language === 'sr' 
                ? 'Odgovorite na pitanja kako bismo odredili primenjive kontrole'
                : 'Answer questions to determine applicable controls'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div 
            className="h-full bg-indigo-600 transition-all duration-300"
            style={{ width: showResults ? '100%' : `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {!showResults ? (
            <div className="space-y-6">
              {/* Step indicator */}
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-md font-medium">
                  {currentStep + 1}/{questions.length}
                </span>
                <span className="flex items-center gap-1">
                  {currentQuestion?.categoryIcon}
                  {currentQuestion?.category.charAt(0).toUpperCase() + currentQuestion?.category.slice(1)}
                </span>
              </div>

              {/* Question */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {language === 'sr' ? currentQuestion?.questionSr : currentQuestion?.question}
                  {currentQuestion?.required && <span className="text-red-500 ml-1">*</span>}
                </h3>
                {currentQuestion?.helpText && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    {language === 'sr' ? currentQuestion.helpTextSr : currentQuestion.helpText}
                  </p>
                )}
              </div>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion?.type === 'single' || currentQuestion?.type === 'yes_no' ? (
                  currentQuestion.options?.map(option => (
                    <label
                      key={option.id}
                      className={`
                        flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                        ${answers[currentQuestion.id] === option.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }
                      `}
                    >
                      <input
                        type="radio"
                        name={currentQuestion.id}
                        value={option.id}
                        checked={answers[currentQuestion.id] === option.id}
                        onChange={() => handleAnswer(currentQuestion.id, option.id)}
                        className="sr-only"
                      />
                      <div className={`
                        w-5 h-5 rounded-full border-2 flex items-center justify-center mr-4
                        ${answers[currentQuestion.id] === option.id
                          ? 'border-indigo-500 bg-indigo-500'
                          : 'border-gray-300 dark:border-gray-600'
                        }
                      `}>
                        {answers[currentQuestion.id] === option.id && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {language === 'sr' ? option.labelSr || option.label : option.label}
                        </span>
                        {option.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {language === 'sr' ? option.descriptionSr || option.description : option.description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))
                ) : currentQuestion?.type === 'multiple' ? (
                  currentQuestion.options?.map(option => {
                    const selected = Array.isArray(answers[currentQuestion.id]) 
                      ? (answers[currentQuestion.id] as string[]).includes(option.id)
                      : false;
                    return (
                      <label
                        key={option.id}
                        className={`
                          flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                          ${selected
                            ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }
                        `}
                      >
                        <input
                          type="checkbox"
                          value={option.id}
                          checked={selected}
                          onChange={(e) => {
                            const current = (answers[currentQuestion.id] as string[]) || [];
                            if (e.target.checked) {
                              handleAnswer(currentQuestion.id, [...current, option.id]);
                            } else {
                              handleAnswer(currentQuestion.id, current.filter(id => id !== option.id));
                            }
                          }}
                          className="sr-only"
                        />
                        <div className={`
                          w-5 h-5 rounded border-2 flex items-center justify-center mr-4
                          ${selected
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300 dark:border-gray-600'
                          }
                        `}>
                          {selected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {language === 'sr' ? option.labelSr || option.label : option.label}
                          </span>
                        </div>
                      </label>
                    );
                  })
                ) : null}
              </div>
            </div>
          ) : (
            /* Results */
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {t.questionnaire.complete}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {language === 'sr'
                    ? `Na osnovu vaših odgovora, odredili smo primenjive kontrole za vašu organizaciju.`
                    : `Based on your answers, we've determined the applicable controls for your organization.`
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl">
                  <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400 mb-1">
                    {applicableControls.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t.questionnaire.applicableControls}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                  <div className="text-3xl font-bold text-gray-600 dark:text-gray-300 mb-1">
                    {controls.length - applicableControls.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {t.questionnaire.excludedControls}
                  </div>
                </div>
              </div>

              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-300">
                    {language === 'sr'
                      ? 'Ove preporuke su početna tačka. Preporučujemo da ih pregledate sa vašim timom za usklađenost.'
                      : 'These recommendations are a starting point. We recommend reviewing them with your compliance team.'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0 && !showResults}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            {t.actions.back}
          </button>

          {!showResults ? (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === questions.length - 1 ? t.questionnaire.viewResults : t.actions.next}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleComplete}
              className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              <Check className="w-4 h-4" />
              {t.questionnaire.applyRecommendations}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FrameworkQuestionnaire;
